import { Button, CircularProgress, Drawer, makeStyles, TextField, Typography } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import IncrementingNumberInput from '../components/IncrementingNumberInput'
import { FlexBreakSmall, useIsSmall } from '../components/MediaHelpers'
import { OrderContext } from '../OrderContext'
import { ExchangeItem } from '../OrderContext/types'
import { ExchangeItemContext } from './ExchangeItemContext'
import { v4 as uuid } from 'uuid'
import { EmptyContainer } from '../components/Table'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme => ({
    paper: {
        width: '75%',
        // padding: 30,
        [theme.breakpoints.down('md')]: {
            width: '100%'
        }
    },
    divider: {
        margin: '30px 0'
    },
    odd: {
        padding: '15px 30px',
        background: theme.palette.grey[200]
    },
    even: {
        padding: '15px 30px',
        background: theme.palette.background.default
    }
}))

const ItemSearch = () => {
    const { showSearch, setShowSearch, exchangeItems, loading } = useContext(ExchangeItemContext)
    const { setPurchases, purchases } = useContext(OrderContext)
    const [search, setSearch] = useState<string>('')
    const [quantities, setQuantities] = useState<Record<string, number>>({})
    const [page, setPage] = useState(1)
    const classes = useStyles()
    const isSmall = useIsSmall()
    const scrollPoint = useMemo(() => uuid(), [])
    const { t } = useTranslation()

    useEffect(() => {
        if (!exchangeItems) return

        setQuantities(exchangeItems.reduce((acc, val) => val.productId 
            ? ({ ...acc, [val.productId]: 1 })
            : acc, {}))
    }, [exchangeItems])

    useEffect(() => { setPage(1) }, [search, exchangeItems])

    const handleSelection = (r: ExchangeItem) => () => {
        setPurchases([...purchases, { ...r, quantity: r.productId ? quantities[r.productId] : 0 }])
        setSearch('')
        setQuantities(exchangeItems.reduce((acc, val) => val.productId 
            ? ({ ...acc, [val.productId]: 1 })
            : acc, {}))
        setShowSearch(false)
    }

    const handleSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setSearch(e.target.value)
    }

    const rows = exchangeItems.filter(i => {
        const term = search.toLocaleUpperCase().trim()

        return i?.productName?.toLocaleUpperCase()?.includes(term) || i.productId === term
    })

    const handlePageChange = (_: unknown, p: number) => {
        setPage(p)
        document.getElementById(scrollPoint)?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <Drawer open={showSearch} anchor='right' PaperProps={{ className: classes.paper }} onClose={() => setShowSearch(false)}>
            <Typography style={{ padding: '15px 30px' }} variant='h5'>{t('choose_exchange')}</Typography>

            <div style={{ padding: '0px 30px 15px' }}>
                <TextField style={{ width: '100%' }} id="outlined-basic" label={t('search')} variant="outlined" value={search} onChange={handleSearch} />
            </div>

            <div id={scrollPoint} style={{ display: 'flex', flexDirection: 'column', marginBottom: 30 }}>
                {loading && <EmptyContainer><CircularProgress size='4em' /></EmptyContainer>}

                {!loading && <>
                    {!isSmall && <div style={{ display: 'flex', gap: 15, padding: '15px 30px' }}>
                        <div style={{ flex: 3 }}><b>{t('description')}</b></div>
                        <div style={{ flex: 1 }}><b>{t('material_number')}</b></div>
                        <div style={{ flex: 1 }}><b>{t('unit_price')}</b></div>
                        <div style={{ flex: 2 }}><b>{t('quantity')}</b></div>
                        <div style={{ flex: 1 }}></div>
                    </div>}

                    {rows.filter(r => r.productId).slice((page - 1) * 10, (page - 1) * 10 + 10).map((item, i) =>
                        <div key={i} className={i % 2 === 1 ? classes.even : classes.odd} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 15, alignItems: 'center' }}>
                            <div style={{ flex: 3 }}>
                                {isSmall && <p><b>Description</b></p>}
                                <p style={{ margin: 0, padding: 0 }}>{item.productName}</p>
                            </div>
                            <FlexBreakSmall />
                            <div style={{ flex: 1 }}>
                                {isSmall && <p><b>Material</b></p>}
                                <p style={{ margin: 0, padding: 0 }}>{item.productId}</p>
                            </div>
                            <div style={{ flex: 1 }}>
                                {isSmall && <p><b>Unit Price</b></p>}
                                <p style={{ margin: 0, padding: 0 }}>{parseFloat(item.materialPrice || '0').toFixed(2)}</p>
                            </div>
                            <FlexBreakSmall />
                            <div style={{ flex: 2 }}>
                                {isSmall && <p><b>Quantity</b></p>}
                                <IncrementingNumberInput min={1} size='small' style={{ width: '100%' }} value={quantities[item.productId as string]} onChange={v => setQuantities({ ...quantities, [item.productId as string]: (v || 0) })} />
                            </div>
                            <FlexBreakSmall />
                            <div style={{ flex: 1 }}><Button variant="contained" color="primary" onClick={handleSelection(item)} style={{ width: isSmall ? '100%' : undefined }}>{t('select')}</Button></div>
                        </div>
                    )}
                </>}
                <div style={{ display: 'flex', flexDirection: 'row-reverse', padding: '15px 30px 0' }}>
                    <Pagination size={isSmall ? 'small' : 'medium'} color='primary' shape='rounded' count={Math.ceil(rows.filter(r => r.productId).length / 10)} page={page} onChange={handlePageChange} />
                </div>
            </div>
        </Drawer>
    )
}

export default ItemSearch
